import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useRef, useState, useEffect, ChangeEvent } from "react";
import { v4 as uuidv4 } from "uuid";
import { Dialog, Switch } from "@mui/material";
import { DialogType } from "app/types";
import { actions } from "app/configSlice";
import { RootState, AppDispatch } from "app/store";
import { updateParentConfig, uploadImageFile } from "app/api";
import Constants from "common/constant";
import { dataURLToBlob } from "common/utils";
import { ParentCloseBtn, FadeTransition } from "common/elements";
import I18n from "features/shared/i18n";
import { selectAllParentChatRooms } from "features/message/slice";
import { ChatRoomType, MessageContentType } from "features/message/types";
import { getParentChatRooms, sendContactChatRoomMessage } from "features/message/api";

const DETAIL_MAX_LENGTH = 500;

// axios のエラーは message だけだと原因が分からないので、status と本文も拾う
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const describeError = (error: any) => {
  const status = error?.response?.status;
  const body = error?.response?.data;
  const text = typeof body === "string" ? body : body ? JSON.stringify(body) : "";
  return [status && `HTTP ${status}`, text, error?.message].filter(Boolean).join(" / ").slice(0, 300);
};

/**
 * 端末を振ったときに出る「うまく動かない？」の報告ダイアログ。
 *
 * この画面だけで完結させる。入力した内容と撮っておいた画面キャプチャを
 * サポートのチャットへそのまま送る（画面遷移しない）。
 * ここで振って報告する機能自体のオン/オフもできる。
 */
export const ShakeBugReportDialog = () => {
  const dispatch = useDispatch<AppDispatch>();
  const parent = useSelector((state: RootState) => state.user.parent);
  const chatRooms = useSelector(selectAllParentChatRooms);
  const shakeBugReport = useSelector(
    (state: RootState) => state.config.parentConfig.shake_bug_report,
  );
  const shake = useSelector((state: RootState) => state.config.dialog.shake_bug_report);
  const capture = shake.args?.capture;

  const [detail, setDetail] = useState("");
  const [sending, setSending] = useState(false);
  // 添付画像。振ったときのキャプチャを初期値にし、外したら選び直せる
  const [image, setImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // ネイティブではコンソールを見られないので、失敗の理由は画面に出す
  const [errorDetail, setErrorDetail] = useState("");

  const contactRoomId = chatRooms.find((room) => room.type === ChatRoomType.CONTACT)?.id;

  useEffect(() => {
    if (shake.value) setImage(capture ?? null);
  }, [shake.value, capture]);

  // アカウント画面などチャットを開いていない画面から振られると、
  // チャットルーム一覧が store に無く送信先が引けない。開いた時点で取りにいく
  useEffect(() => {
    if (shake.value && !contactRoomId && parent?.uid) {
      dispatch(getParentChatRooms({ uid: parent.uid }));
    }
  }, [shake.value, contactRoomId, parent?.uid, dispatch]);

  const handleClose = () => {
    setDetail("");
    setImage(null);
    setErrorDetail("");
    dispatch(actions.updateDialog({ type: DialogType.SHAKE_BUG_REPORT, value: false, args: {} }));
  };

  const handleChangeDetail = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setDetail(e.target.value.slice(0, DETAIL_MAX_LENGTH));
  };

  const handleToggleShake = (_: unknown, checked: boolean) => {
    dispatch(actions.updateParentConfig({ key: "shake_bug_report", value: checked }));
    if (parent?.uid) dispatch(updateParentConfig({ uid: parent.uid }));
  };

  // 添付は必須ではないので、アップロードに失敗しても本文だけ送る
  const uploadCapture = async () => {
    if (!image || !parent?.uid) return undefined;
    try {
      const filename = `parents/${parent.uid}/shake_capture/${uuidv4()}.jpg`;
      await uploadImageFile({
        fileBlob: dataURLToBlob(image),
        filename,
        contentType: "image/jpeg",
      });
      return `${Constants.assetHost}/${filename}`;
    } catch (error) {
      console.error("Failed to upload capture:", error);
      setErrorDetail(`upload: ${describeError(error)}`);
      return undefined;
    }
  };

  const handleSend = async () => {
    if (sending) return;
    if (!parent?.uid) {
      setErrorDetail("uid なし（ログイン状態を確認してください）");
      return;
    }
    if (!contactRoomId) {
      setErrorDetail("サポートのチャットが見つかりません（room 未取得）");
      return;
    }
    setSending(true);
    setErrorDetail("");
    try {
      const imageUrl = await uploadCapture();
      const text = detail.trim();
      const hasImage = !!imageUrl;
      await sendContactChatRoomMessage({
        uid: parent.uid,
        roomId: contactRoomId,
        message: {
          url: `${window.location.pathname}${window.location.search}`,
          message_content_type: hasImage
            ? text
              ? MessageContentType.IMAGE_WITH_TEXT
              : MessageContentType.IMAGE
            : MessageContentType.TEXT,
          message_content: hasImage ? (text ? { url: imageUrl, text } : { url: imageUrl }) : { text },
        },
      });
      toast(I18n.t("MSG_PARENTS_SHAKE_BUG_REPORT_SENT"));
      handleClose();
    } catch (error) {
      console.error("Failed to send bug report:", error);
      setErrorDetail(`send: ${describeError(error)}`);
    } finally {
      setSending(false);
    }
  };

  // 添付は任意。本文があれば送れる（ルーム未取得は押した時に理由を出す）
  const canSend = !sending && (!!detail.trim() || !!image);

  const handlePickImage = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setImage(String(reader.result));
      reader.readAsDataURL(file);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <Dialog
      fullScreen
      maxWidth={false}
      open={shake.value}
      sx={{ zIndex: 99999 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
      onClose={handleClose}
    >
      {/* スマホが主なので中央のカードではなく下から出すシートにする。
          max-h と overflow-y で、キーボードが出ても画面からはみ出さない */}
      <div className="flex-col-el justify-end items-center w-full h-full bg-textcolor-black/50">
        <div className="flex-col-el w-full max-w-[600px] max-h-[85%] overflow-y-auto rounded-t-[20px] bg-white">
          <div className="flex-col-el justify-start w-full relative box-border px-5 pt-5 pb-8">
            <ParentCloseBtn close={handleClose} />

              <div className="flex-row-el items-center gap-2 mb-4 pb-3 border-b border-outline shrink-0">
                <span className="material-symbols-outlined !text-[34px] text-blue-100">
                  bug_report
                </span>
                <h2 className="text-[22px] text-textcolor-black">
                  {I18n.t("MSG_PARENTS_SHAKE_BUG_REPORT_TITLE")}
                </h2>
              </div>

              <p className="text-body text-textcolor-black text-[15px] mb-3">
                {I18n.t("MSG_PARENTS_SHAKE_BUG_REPORT_DESCRIPTION")}
              </p>

              <textarea
                rows={4}
                value={detail}
                onChange={handleChangeDetail}
                placeholder={I18n.t("MSG_PARENTS_SHAKE_BUG_REPORT_PLACEHOLDER")}
                className="resize-none w-full box-border p-3 rounded-[12px] border border-solid border-gray2-60 text-[15px] text-textcolor-black"
              />

              {/* 添付の見た目と外し方はチャットの MessageImagePreview に合わせる */}
              {/* 縦長の画面をそのまま並べると細い帯になって余白だらけになるので、
                  小さな正方形に切り出した 1 行にする（縦横比に左右されない） */}
              {/* 枠の大きさは常に同じ。外したら選び直せるようにボタンを出す。
                  ボタン操作で下の要素が動かないよう、中身だけ差し替える */}
              <div className="flex-row-el items-center gap-3 w-full h-[80px] box-border mt-3 p-2 rounded-[12px] border border-solid border-gray2-60">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePickImage}
                  className="hidden"
                />
                {image ? (
                  <>
                    <img
                      alt=""
                      src={image}
                      className="w-[48px] h-full shrink-0 object-contain rounded-[6px] bg-gray2-20"
                    />
                    <p className="flex-1 text-body text-textcolor-black text-[14px]">
                      {I18n.t("MSG_PARENTS_SHAKE_BUG_REPORT_ATTACH_LABEL")}
                    </p>
                    <button
                      onClick={() => setImage(null)}
                      aria-label={I18n.t("MSG_PARENTS_SHAKE_BUG_REPORT_ATTACH_REMOVE")}
                      className="flex-col-el flex-center shrink-0 w-8 h-8 rounded-full bg-gray2-30 border-none cursor-pointer"
                    >
                      <span className="material-symbols-outlined text-textcolor-black !text-[20px]">
                        close
                      </span>
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex-col-el flex-center w-[48px] h-full shrink-0 rounded-[6px] bg-gray2-20">
                      <span className="material-symbols-outlined text-gray2-100 !text-[20px]">
                        hide_image
                      </span>
                    </div>
                    <p className="flex-1 text-body text-gray2-100 text-[14px]">
                      {I18n.t("MSG_PARENTS_SHAKE_BUG_REPORT_ATTACH_OFF")}
                    </p>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="flex-row-el items-center justify-center gap-1 shrink-0 px-3 py-2 rounded-full bg-gray2-30 border-none cursor-pointer text-[13px] text-textcolor-black"
                    >
                      <span className="material-symbols-outlined text-textcolor-black !text-[18px]">
                        add_photo_alternate
                      </span>
                      {I18n.t("MSG_PARENTS_SHAKE_BUG_REPORT_ATTACH_PICK")}
                    </button>
                  </>
                )}
              </div>

              <div className="flex-row-el flex-center w-full mt-5">
                <button
                  onClick={handleSend}
                  disabled={!canSend}
                  className="flex-row-el items-center justify-center gap-2 w-full px-6 py-3 bg-blue-100 border-none text-white rounded-[12px] text-[16px] cursor-pointer disabled:bg-gray2-60 disabled:cursor-not-allowed hover:opacity-80 transition-all"
                >
                  <span className="material-symbols-outlined !text-[20px] text-white">send</span>
                  {sending
                    ? I18n.t("MSG_PARENTS_SHAKE_BUG_REPORT_SENDING")
                    : I18n.t("MSG_PARENTS_SHAKE_BUG_REPORT_BTN")}
                </button>
              </div>

              {errorDetail && (
                <p className="text-body text-red-100 text-[13px] break-all mt-3">
                  {I18n.t("MSG_PARENTS_SHAKE_BUG_REPORT_SEND_FAILED")}
                  {"\n"}
                  {errorDetail}
                </p>
              )}

              <div className="flex-col-el gap-1 mt-5 pt-4 border-t border-outline">
                <div className="flex-row-el items-center justify-between gap-4 w-full">
                  <p className="text-body text-textcolor-black text-[15px]">
                    {I18n.t("MSG_PARENTS_SHAKE_BUG_REPORT_TOGGLE_LABEL")}
                  </p>
                  <Switch
                    checked={!!shakeBugReport}
                    onChange={handleToggleShake}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: "#4c9df1" },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: "#4c9df1",
                      },
                    }}
                  />
                </div>
                <p className="text-body text-gray2-100 text-[13px]">
                  {I18n.t("MSG_PARENTS_SHAKE_BUG_REPORT_TOGGLE_DESCRIPTION")}
                </p>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
